// 🔧 auction_item.js
// ✅ Purpose: Public-facing Silent Auction item bidding page
// ✅ Features:
// - Loads one auction item from /events/{eventId}/auctionItems/{itemId}
// - Respects auctionEnabled setting
// - Only allows bids when auction item status is "open"
// - Requires user to be logged in before bidding
// - Calculates minimum next bid using currentBid/startBid + minimumIncrement
// - Submits bids through an atomic server-side callable
// - Supports linked voting entries through linkedEntryId and linkedEntryLabel
// - Keeps future payment fields separate from bidding logic
// - Uses success/error messages at the top of the page
// - Includes console logging for debugging
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  doc,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { AUCTION_STATUS, getAuctionStatus, getAuctionStatusLabel, isPublicAuctionItem, getMinimumAuctionBidCents } from "../../utils/auction_status";
import { getEntryImageUrls } from "../../utils/entry_images";
import AuctionBidderRegistration from "../../components/auction_bidder_registration";
import { useAuctionRegistration } from "../../hooks/use_auction_registration";
import { getPowerupPath, resolvePowerupSnapshot } from "../../utils/powerup_urls";

const AuctionItem = () => {
    
  const { itemId } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
    eventDoc,
    } = useEventContext({ redirectIfNotFound: true });

  const auctionEnabled = hasPurchasedAddOn(
    eventDoc,
    "auction"
  );
  const [auctionItem, setAuctionItem] = useState(null);
  const [canonicalAuctionItemId, setCanonicalAuctionItemId] = useState("");
  const [loadingItem, setLoadingItem] = useState(true);
  const [placingBid, setPlacingBid] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [bidHistory, setBidHistory] = useState([]);
  const [countdownNow, setCountdownNow] = useState(new Date());
  const [bidderCode, setBidderCode] = useState("");
  const [linkedEntry, setLinkedEntry] = useState(null);
  const [ownOrder, setOwnOrder] = useState(null);
  const registration = useAuctionRegistration({
    eventId, required: eventDoc?.auctionRegistrationRequired === true, user,
    onMessage: (type, message) => type === "success" ? showSuccess(message) : showError(message),
  });

  // Read the canonical voting Entry so older auction snapshots and later image
  // changes use the same gallery. Missing/deleted Entries retain saved images.
  useEffect(() => {
    if (!ready || !eventId || !auctionItem?.linkedEntryId || !auctionItem.published) return;
    const key = `${eventId}:${auctionItem.linkedEntryId}`;
    return onSnapshot(doc(db, "events", eventId, "cars", auctionItem.linkedEntryId), (snapshot) => {
      setLinkedEntry({ key, data: snapshot.exists() ? snapshot.data() : null });
    }, (err) => {
      logger.error("Failed to load linked auction Entry images", { value: err });
      setLinkedEntry({ key, data: null });
    });
  }, [ready, eventId, auctionItem?.linkedEntryId, auctionItem?.published]);

  // =====================================================
  // 🏷️ Auction Status Logic
  // =====================================================
  // ✅ Purpose:
  // - Bidding is only allowed when the event auction window is active
  // - Bidding is only allowed when the item status is open
  // - Public users can still view items before/after auction window
  // =====================================================

  const now = countdownNow;
  const asDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const auctionStartDate = asDate(auctionItem?.auctionStart || eventDoc?.auctionStart);
  const auctionEndDate = asDate(auctionItem?.auctionEnd || eventDoc?.auctionEnd);
  const auctionHasTimer = Boolean((auctionStartDate || auctionItem?.biddingStartsImmediately === true) && auctionEndDate);
  const internalStatus = String(auctionItem?.status || "draft").toLowerCase();
  const auctionFinished = internalStatus === "closed" || Boolean(auctionEndDate && now >= auctionEndDate);
  const auctionWaiting = !auctionFinished && (
    internalStatus === "draft" || Boolean(auctionItem?.biddingStartsImmediately !== true && auctionStartDate && now < auctionStartDate)
  );
  const auctionOpen = !auctionWaiting && !auctionFinished && internalStatus === "open";
  const biddingAllowed = auctionHasTimer && auctionOpen;

  useEffect(() => {
    const auctionPaymentStatus = searchParams.get("auction_payment");

    if (auctionPaymentStatus === "success") {
      showSuccess(
        "✅ Auction payment completed. Thank you! The event team can now prepare this item for pickup."
      );
    }

    if (auctionPaymentStatus === "cancelled") {
      showError(
        "Auction payment was cancelled. You can still complete payment using the link from your email."
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    
    if (!ready || !eventId || !itemId) {
      setLoadingItem(false);
      return;
    }

    setCanonicalAuctionItemId("");
    const unsubscribeItem = loadAuctionItem();

    return () => {
      unsubscribeItem?.();
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventId, itemId]);

  useEffect(() => {
    if (!ready || !eventId || !user?.uid) {
      setBidderCode("");
      return;
    }

    const getIdentity = httpsCallable(functions, "getAuctionBidderIdentity");
    getIdentity({ eventId })
      .then((result) => setBidderCode(result.data?.bidderCode || ""))
      .catch((err) => logger.error("Failed loading bidder identity", { value: err }));
  }, [ready, eventId, user?.uid]);

  // This duplicate-safe owner query uses the same Rule-supported shape as the
  // listing page. Never read allocations or another buyer's order on detail.
  useEffect(() => {
    if (!ready || !eventId || !canonicalAuctionItemId || !user?.uid) {
      setOwnOrder(null);
      return undefined;
    }
    return onSnapshot(query(
      collection(db, "events", eventId, "orders"),
      where("uid", "==", user.uid),
      where("orderType", "==", "auction")
    ), (snapshot) => {
      setOwnOrder(snapshot.docs.map((record) => ({ ...record.data(), id: record.id }))
        .find((order) => order.auctionItemId === canonicalAuctionItemId) || null);
    }, (err) => {
      logger.error("Failed to load own auction order", { value: err });
      setOwnOrder(null);
    });
  }, [ready, eventId, canonicalAuctionItemId, user?.uid]);

  useEffect(() => {
    if (!ready || !eventId || !canonicalAuctionItemId) {
      setBidHistory([]);
      return undefined;
    }
    return loadBidHistory(canonicalAuctionItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventId, canonicalAuctionItemId]);

  // =====================================================
  // 📊 Google Analytics
  // Record when an auction item is viewed.
  // =====================================================
  useEffect(() => {
    if (!ready || !eventId || !auctionItem?.id) {
      return;
    }

    trackEvent({
      action: GA_EVENTS.AUCTION_ITEM_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        item_id: auctionItem.id,
        item_status: auctionItem.status || "",
        current_bid: Number(auctionItem.currentBid || 0),
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, auctionItem]);

    // =====================================================
    // ⏳ Live Auction Countdown Clock
    // =====================================================
    // ✅ Purpose:
    // - Updates the public auction timer every second
    // - Supports "starts in", "ends in", and "ended" display
    // =====================================================

    useEffect(() => {
    const timer = setInterval(() => {
        setCountdownNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
    }, []);  

  const showSuccess = (message) => {
    setStatus(message);
    setError("");

    setTimeout(() => {
      setStatus("");
    }, 20000);
  };

  const showError = (message) => {
    setError(message);
    setStatus("");

    setTimeout(() => {
      setError("");
    }, 20000);
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const getDisplayCurrentBid = (item) => {
    if (!item) return 0;
    const current = Number(item.currentBidCents ?? Math.round(Number(item.currentBid || 0) * 100));
    return current > 0 ? current / 100 : Number(item.startingBidCents ?? Math.round(Number(item.startingBid || 0) * 100)) / 100;
  };

  const getMinimumNextBid = (item) => {
    if (!item) return 0;

    return getMinimumAuctionBidCents(item) / 100;
  };

    // =====================================================
    // 🏷️ Auction Timer Helpers
    // =====================================================
    // ✅ Purpose:
    // - Reads event-level auctionStart and auctionEnd from eventDoc
    // - Determines if public bidding is currently allowed
    // - Works alongside item status === "open"
    // =====================================================

    const getDateFromPossibleTimestamp = (value) => {
    if (!value) return null;

    if (value.toDate) {
        return value.toDate();
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
    };

    const getAuctionTimerStatus = () => {
    const now = countdownNow;

    const start = getDateFromPossibleTimestamp(
      auctionItem?.auctionStart || eventDoc?.auctionStart
    );
    const end = getDateFromPossibleTimestamp(
      auctionItem?.auctionEnd || eventDoc?.auctionEnd
    );

    if (auctionItem?.biddingStartsImmediately !== true && start && now < start) {
        return {
        allowed: false,
        message: `Auction bidding opens ${start.toLocaleString()}.`,
        };
    }

    if (end && now >= end) {
        return {
        allowed: false,
        message: `Auction bidding closed ${end.toLocaleString()}.`,
        };
    }

    return {
        allowed: true,
        message: "",
    };
    };

  const loadAuctionItem = () => {
    try {
      setLoadingItem(true);

      logger.log("🔎 Loading auction item", {
        value: {
          eventId,
          itemId,
        },
      });

      let unsubscribe;
      resolvePowerupSnapshot(db, eventId, "auction", itemId).then((resolved) => {
        if (!resolved?.exists()) {
          showError("❌ Auction item not found.");
          setAuctionItem(null);
          setLoadingItem(false);
          return;
        }
        setCanonicalAuctionItemId(resolved.id);
        unsubscribe = onSnapshot(resolved.ref, (itemSnap) => {

      if (!itemSnap.exists()) {
        showError("❌ Auction item not found.");
        setAuctionItem(null);
        setLoadingItem(false);
        return;
      }

      const loadedItem = {
        id: itemSnap.id,
        ...itemSnap.data(),
      };

      setAuctionItem(loadedItem);

      const minimumNextBid = getMinimumNextBid(loadedItem);
      setBidAmount(minimumNextBid ? String(minimumNextBid) : "");

      logger.log("✅ Auction item loaded", {
        value: loadedItem,
      });
      setLoadingItem(false);
        }, (err) => {
      logger.error("❌ Failed to load auction item", {
        value: err,
        });
      }); }).catch((err) => {
        logger.error("❌ Failed to resolve auction item", { value: err });
        showError("❌ Failed to load auction item.");
        setLoadingItem(false);
      });
      return () => unsubscribe?.();
    } catch (err) {
      logger.error("Unable to start live auction item", { value: err });
      setLoadingItem(false);
      return undefined;
    }
  };

  // =====================================================
    // 📜 Load recent bid history
    // =====================================================
    // Purpose:
    // - Shows recent activity on the auction item page
    // - Keeps bidder email private from public view
    // - Helps users trust that bidding is being tracked
    // =====================================================

    const loadBidHistory = (auctionItemId) => {
    try {
        const bidsRef = collection(db, "events", eventId, "auctionBidActivity");

        const bidHistoryQuery = query(
        bidsRef,
        where("itemId", "==", auctionItemId),
        orderBy("createdAt", "desc"),
        limit(10)
        );

        return onSnapshot(bidHistoryQuery, (snap) => {

        const loadedBids = snap.docs.map((bidDoc) => ({
        id: bidDoc.id,
        ...bidDoc.data(),
        }));

        setBidHistory(loadedBids);

        logger.log("✅ Bid history loaded", {
        value: loadedBids.length,
        });
        }, (err) => {
        logger.error("❌ Failed to load bid history", {
        value: err,
        });
        });
    } catch (err) {
        logger.error("Unable to start live bid history", { value: err });
        return undefined;
    }
    };

    const getCountdownParts = (targetDate) => {
    if (!targetDate) {
        return null;
    }

    const difference = targetDate.getTime() - countdownNow.getTime();

    if (difference <= 0) {
        return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / (1000 * 60)) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return {
        days,
        hours,
        minutes,
        seconds,
    };
    };

  const placeBid = async (e) => {
    e.preventDefault();

    try {
      setPlacingBid(true);
      setStatus("");
      setError("");

        if (!biddingAllowed) {
        showError("❌ Bidding is not currently available for this auction item.");
        return;
        }

      if (!auctionEnabled) {
        showError("❌ Silent Auction is not enabled for this event.");
        return;
      }

      if (!auctionItem) {
        showError("❌ Auction item could not be loaded.");
        return;
      }

      if (auctionItem.status !== "open") {
        showError("❌ This auction item is not currently open for bidding.");
        return;
      }

        const timerStatus = getAuctionTimerStatus();

        if (!timerStatus.allowed) {
        showError(`❌ ${timerStatus.message}`);
        return;
        }
        
      if (!user) {
        showError("❌ Please log in before placing a bid.");
        return;
      }

      const enteredBid = Number(bidAmount);
      const minimumNextBid = getMinimumNextBid(auctionItem);

      if (!Number.isFinite(enteredBid) || enteredBid <= 0) {
        showError("❌ Please enter a valid bid amount.");
        return;
      }

      if (enteredBid < minimumNextBid) {
        showError(`❌ Minimum bid is ${formatMoney(minimumNextBid)}.`);
        return;
      }

      if (eventDoc?.auctionRegistrationRequired === true && !(await registration.checkBeforeBid())) return;

      if (!window.confirm(`Place a bid of ${formatMoney(enteredBid)}? This cannot be withdrawn.`)) return;

      const placeAuctionBid = httpsCallable(functions, "placeAuctionBid");
      const bidResult = await placeAuctionBid({
        eventId,
        auctionItemId: auctionItem.id,
        bidAmountCents: Math.round(enteredBid * 100),
      });

      setBidderCode(bidResult.data?.bidderCode || bidderCode);

      showSuccess("✅ Bid placed successfully.");

      trackEvent({
        action: GA_EVENTS.AUCTION_BID_PLACED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          item_id: auctionItem.id,
          bid_amount: enteredBid,
          minimum_next_bid: minimumNextBid,
          source_page: window.location.pathname,
        },
      });

    } catch (err) {
      logger.error("❌ Failed to place auction bid", {
        value: err,
      });

      showError("❌ Failed to place bid.");
    } finally {
      setPlacingBid(false);
    }
  };

  if (!ready || eventLoading || loadingItem) {
    return <FullPageSpinner message="Loading Auction Item..." />;
  }

  if (!auctionEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="alert alert-warning">
          Silent Auction is not enabled for this event.
        </div>
      </section>
    );
  }

  if (!auctionItem) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="alert alert-danger">
          Auction item not found.
        </div>

        <Link className="btn btn-outline-secondary" to={`/${eventslug}/auction`}>
          Back to Auction
        </Link>
      </section>
    );
  }

  const publicItemStatus = getAuctionStatus({ item: auctionItem, event: eventDoc, now });
  if (auctionItem.published !== true || !isPublicAuctionItem(publicItemStatus)) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="alert alert-info">This auction item is not published.</div>
        <Link className="btn btn-outline-secondary" to={`/${eventslug}/auction`}>
          Back to Auction
        </Link>
      </section>
    );
  }

  const entryImages = linkedEntry?.key === `${eventId}:${auctionItem.linkedEntryId}` && linkedEntry.data
    ? getEntryImageUrls(linkedEntry.data) : [];
  const imageUrls = entryImages.length ? entryImages : getEntryImageUrls(auctionItem);

      const breadcrumbs = [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Auction",
        path: `/${eventslug}/auction`,
      },
      {
        name:
          auctionItem?.title ||
          auctionItem?.linkedEntryLabel ||
          "Auction Item",
        path: getPowerupPath(eventslug, "auction", auctionItem),
      },
    ];

    const approvedAuctionItemSeo = buildEventPageSeo({
      eventDoc,
      pageKey: "auctionItem",
      fallbackTitle: `${
        auctionItem?.title ||
        auctionItem?.linkedEntryLabel ||
        "Auction Item"
      } | ${eventDoc?.name || "Event"} | Let's Go Vote`,
      fallbackDescription:
        auctionItem?.description ||
        `View auction item details and bidding information for ${
          eventDoc?.name || "this event"
        }.`,
    });

    const auctionItemSeo = {
      ...approvedAuctionItemSeo,
      path: getPowerupPath(eventslug, "auction", auctionItem),
      image:
        imageUrls[0] ||
        eventDoc?.eventLogo ||
        "https://letsgovote.app/images/social-share-silent-auctions.png",
      breadcrumbs,
    };

    const displayCurrentBid = getDisplayCurrentBid(auctionItem);
    const minimumNextBid = getMinimumNextBid(auctionItem);
    const canBidNow = biddingAllowed;
    const timerStatus = getAuctionTimerStatus();
    const publicState = getAuctionStatus({ item: auctionItem, event: eventDoc, now });
    const countdownTarget = auctionWaiting ? auctionStartDate : auctionEndDate;
    const countdownParts = getCountdownParts(countdownTarget);
    const countdownLabel = auctionWaiting ? "Auction Starts In" : "Auction Ends In";
    const finalBid = Number(auctionItem.settlementPriceCents ?? auctionItem.currentBidCents ?? Math.round(Number(auctionItem.currentBid || 0) * 100)) / 100;

  return (
    <>
    <SeoHead {...auctionItemSeo} />  
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🏷️ Silent Auction Item</h1>

          <p className="lead text-muted">
            Review the item details and place your bid.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

      {status && <div className="alert alert-success" role="alert">{status}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {ownOrder?.status === "paid" && ownOrder.fulfillmentStatus === "ready_for_pickup" && (
        <div className="alert alert-success" role="status">
          Payment received — this item is ready for pickup. <Link to={`/${eventslug}/my_orders?type=auction#order-${ownOrder.id}`}>View order details</Link>
        </div>
      )}
      <AuctionBidderRegistration {...registration.panelProps} />

      {/* =====================================================
          🏷️ Auction Timer Status Messages
      ===================================================== */}

      {!auctionHasTimer && (
      <div className="alert alert-warning">
          🕒 Auction timer has not been configured yet. Bidding is not currently available.
      </div>
      )}

      {auctionHasTimer && auctionWaiting && (
      <div className="alert alert-info">
          🕒 This auction has not started yet.
      </div>
      )}

      {auctionHasTimer && auctionFinished && (
      <div className="alert alert-secondary">
          🏁 This auction has ended.
      </div>
      )}

      {auctionHasTimer && !auctionWaiting && !auctionFinished && !auctionOpen && (
      <div className="alert alert-warning">
          🏷️ This auction item is not currently open for bidding.
      </div>
      )}

      <div className="row g-4">

          <div className="col-lg-7">
            <div className="card shadow-sm">
              {imageUrls.map((imageUrl, index) => (
                <img
                  key={imageUrl}
                  src={imageUrl}
                  alt={`${auctionItem.title || "Auction item"}${index ? ` ${index + 1}` : ""}`}
                  className="card-img-top"
                />
              ))}

              <div className="card-body">
                <h2 className="h4">
                  {auctionItem.title || auctionItem.linkedEntryLabel}
                </h2>

                {auctionItem.linkedEntryLabel && (
                  <div className="text-muted mb-3">
                    Linked Entry: {auctionItem.linkedEntryLabel}
                  </div>
                )}

                {auctionItem.description && (
                  <p>{auctionItem.description}</p>
                )}

                {auctionItem.auctionNotes && (
                  <div className="alert alert-secondary mt-3">
                    <strong>Auction Notes:</strong>
                    <br />
                    {auctionItem.auctionNotes}
                  </div>
                )}

                {auctionItem.linkedEntryId && (
                  <div className="mt-3">
                    <Link
                      className="btn btn-outline-primary btn-sm"
                      to={`/${eventslug}/vote/${auctionItem.linkedEntryId}`}
                    >
                      View Voting Entry
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="border rounded shadow-sm bg-white h-100">
              {auctionFinished && (
                <div className="p-3 border-bottom">
                  <h2 className="h5">Winning Bid</h2>
                  <div className="fs-4 fw-bold">{finalBid > 0 ? formatMoney(finalBid) : "No winning bid"}</div>
                </div>
              )}

              {!auctionFinished && <>
              <div className="p-3 border-bottom bg-light">
                <strong>Current Auction Status</strong>
              </div>

              <div className="p-3">

                {publicState === AUCTION_STATUS.STARTING_SOON && <div className="mb-3">
                  <span className="badge bg-warning text-dark">
                    {getAuctionStatusLabel(publicState)}
                  </span>
                </div>}

                  {/* =====================================================
                      ⏳ Live Auction Countdown Display
                  ===================================================== */}

                  {auctionHasTimer && !auctionFinished && countdownParts && (
                  <div className="alert alert-light border mb-3">
                      <div className="fw-bold mb-2">
                      {countdownLabel}
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                          <div className="text-center border rounded p-2">
                          <div className="h5 mb-0">{countdownParts.days}</div>
                          <div className="small text-muted">Days</div>
                          </div>

                          <div className="text-center border rounded p-2">
                          <div className="h5 mb-0">{countdownParts.hours}</div>
                          <div className="small text-muted">Hours</div>
                          </div>

                          <div className="text-center border rounded p-2">
                          <div className="h5 mb-0">{countdownParts.minutes}</div>
                          <div className="small text-muted">Minutes</div>
                          </div>

                          <div className="text-center border rounded p-2">
                          <div className="h5 mb-0">{countdownParts.seconds}</div>
                          <div className="small text-muted">Seconds</div>
                          </div>
                      </div>
                  </div>
                  )}

                {auctionWaiting ? (
                  <div className="mb-2">
                    <strong>Starting Bid:</strong>{" "}
                    {formatMoney(auctionItem.startingBid || 0)}
                  </div>
                ) : (
                  <>
                    <div className="mb-2">
                      <strong>Current Bid:</strong>{" "}
                      {formatMoney(displayCurrentBid)}
                    </div>

                    {!auctionFinished && (
                      <div className="mb-2">
                        <strong>Minimum Next Bid:</strong>{" "}
                        {formatMoney(minimumNextBid)}
                      </div>
                    )}

                    <div className="mb-2">
                      <strong>Bid Count:</strong>{" "}
                      {auctionItem.bidCount || 0}
                    </div>
                  </>
                )}

                {Number(auctionItem.quantityAvailable || 1) > 1 && (
                  <div className="mb-2">
                    <strong>Quantity Available:</strong>{" "}
                    {auctionItem.quantityAvailable}. The top unique bidders each win one.
                  </div>
                )}

                {bidderCode && (
                  <div className="alert alert-info py-2 mt-3 mb-2">
                    Your Bidder ID: <strong>{bidderCode}</strong>
                  </div>
                )}

                  {!biddingAllowed && !auctionFinished && (
                  <div className="alert alert-warning">
                      Auction is currently not accepting bids.
                  </div>
                  )}
                  
                {!user && (
                  <div className="alert alert-info">
                    Please log in before placing a bid.
                  </div>
                )}

                  {!auctionOpen && !auctionFinished && (
                  <div className="alert alert-warning">
                      This auction item is not currently open for bidding.
                  </div>
                  )}

                  {auctionOpen && !timerStatus.allowed && (
                  <div className="alert alert-warning">
                      {timerStatus.message}
                  </div>
                  )}

                  {auctionItem.status !== "closed" && user && canBidNow && (
                  <form onSubmit={placeBid}>
                      <div className="mb-3">
                      <label className="form-label" htmlFor="auction-bid-amount">
                        Your Bid Amount
                      </label>

                      <input
                        id="auction-bid-amount"
                        type="number"
                        className="form-control"
                        min={minimumNextBid}
                        step="0.01"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                      />

                      <div className="small text-muted mt-1">
                        Your bid must be at least {formatMoney(minimumNextBid)}.
                      </div>
                    </div>

                      <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!biddingAllowed || placingBid || registration.checking}
                      >
                      {registration.checking ? "Checking registration..." : placingBid ? "Submitting..." : "Place Bid"}
                      </button>

                  </form>
                )}
              </div>
              </>}

              {/* 📜 Recent Bid History */}
              <div className="border rounded shadow-sm mt-4">
                <div className="p-3 border-bottom bg-light">
                  <strong>Recent Bids</strong>
                </div>

                <div className="p-3">

                  {bidHistory.length === 0 ? (
                  <div className="text-muted">
                      No bids have been placed yet.
                  </div>
                  ) : (
                  <div className="list-group">
                      {bidHistory.map((bid) => (
                      <div
                          key={bid.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                      >
                          <div>
                          <strong>Silently Recorded</strong>

                          <div className="small text-muted">
                              {asDate(bid.createdAt) ? (
                                <time dateTime={asDate(bid.createdAt).toISOString()}>
                                  {asDate(bid.createdAt).toLocaleString()}
                                </time>
                              ) : "Time unavailable"}
                          </div>
                          </div>

                          <strong>{formatMoney(Number(bid.amountCents ?? Math.round(Number(bid.amount || 0) * 100)) / 100)}</strong>
                      </div>
                      ))}
                  </div>
                  )}
                </div>
              </div>

              <div className="p-3 border-top">
                <Link
                  className="btn btn-outline-secondary w-100"
                  to={`/${eventslug}/auction`}
                >
                  Back to Auction Items
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default AuctionItem;
